import GenerateWithAIForm, { AIGenerationType } from "@/content/ai/GenerateWithAIForm"
import { GenerateWithAIFormState } from "@/content/ai/button/GenerateWithAIButton"
import FormStore from "@/core/form/store/FormStore"
import CreatePostModal from "@/post/add/CreatePostModal"
import { GlobalID } from "@/relay/RelayTypes"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import AIButton from "@components/ai/AIButton"
import { DiscoModal, DiscoTooltip } from "@disco-ui"
import { OverridableDiscoButtonChildren } from "@disco-ui/button/OverridableDiscoButton"
import { TestIDProps } from "@utils/typeUtils"
import { observer } from "mobx-react-lite"
import { useState } from "react"

interface Props extends TestIDProps {
  feedId: GlobalID
  refetch?: VoidFunction
  children?: OverridableDiscoButtonChildren
}

function WritePostWithAIButton({
  feedId,
  refetch,
  children,
  testid = "WritePostWithAIButton",
}: Props) {
  const [showAIModal, setShowAIModal] = useState(false)
  const [showPostModal, setShowPostModal] = useState(false)
  const classes = useStyles()

  const [form] = useState(() => {
    const initialFormState: GenerateWithAIFormState = {
      prompt: "",
      referenceUrl: "",
      referenceEmbeddingSourceIds: [],
      entityId: "curriculumId",
      assetEmbeddingSourceIds: [],
      contentEmbeddingSourceIds: [],
      trainingDataEmbeddingSourceIds: [],
      uploadedAssetIds: [],
      uploadedAssets: [],
      searchWeb: true,
    }
    const noOpCommitter = () => {
      return {
        dispose: () => {
          /* noop */
        },
      }
    }
    return new FormStore<GenerateWithAIFormState, any>(
      initialFormState,
      noOpCommitter as any,
      {}
    )
  })
  const handleButtonClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    form.state.prompt = ""
    form.state.referenceUrl = ""
    form.state.searchWeb = true
    setShowAIModal(true)
  }

  const handleAIFormSubmit = () => {
    if (!form.state.prompt?.trim()) {
      return
    }

    setShowAIModal(false)
    setTimeout(() => {
      setShowPostModal(true)
    }, 100)
  }

  const handleCloseAIModal = () => {
    setShowAIModal(false)
  }

  const handleClosePostModal = () => {
    setShowPostModal(false)
  }

  return (
    <>
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        style={{ display: "inline-block" }}
        role={"button"}
        tabIndex={0}
      >
        <DiscoTooltip content={"Write with AI"}>
          <AIButton
            variant={"outlined"}
            transparentBg={true}
            testid={testid}
            onClick={handleButtonClick}
            size={"small"}
            stopPropagation={true}
            className={classes.noBorderAIButton}
          >
            {children}
          </AIButton>
        </DiscoTooltip>
      </div>

      <DiscoModal
        testid={`${testid}.AIFormModal`}
        modalContentLabel={"Post Generation"}
        title={"Describe your post idea"}
        width={"720px"}
        maxWidth={"90vw"}
        isOpen={showAIModal}
        onClose={handleCloseAIModal}
        body={
          <GenerateWithAIForm
            form={form}
            onSubmit={handleAIFormSubmit}
            testid={`${testid}.AIForm`}
            generationType={AIGenerationType.Post}
          />
        }
      />

      {showPostModal && (
        <CreatePostModal
          isOpen={showPostModal}
          onClose={handleClosePostModal}
          feedId={feedId}
          refetch={refetch}
          aiTemplate={"default"}
          aiPrompt={form.state.prompt}
          aiReferenceUrl={form.state.referenceUrl || undefined}
          aiSearchWeb={form.state.searchWeb || undefined}
        />
      )}
    </>
  )
}

const useStyles = makeUseStyles(() => ({
  noBorderAIButton: {
    width: "40px",
    height: "40px",
  },
}))

export default observer(WritePostWithAIButton)
